import React, { useState } from "react";
import { useLocation } from 'react-router';
import { Link } from "react-router-dom";
import * as CaptchaLib from "@repoflow/browser/captcha";
import { useCaptcha } from "@repoflow/components/captcha";

import AuthSchema from "@common/schema/auth";
import * as Loading from "@repoflow/components/loading";

import { Component as Form } from "@repoflow/form-schema";
import { Container, Col, Row, Alert } from "reactstrap";
import * as Section from "@repoflow/components/section";
import { gql } from "@apollo/client";

import { MissingDomain } from "../login";

const REQUEST_RESET = gql`
  mutation RequestReset(
    $email: String!
    $domainId: String!
    $token: String!
  ) {
    viewer {
      anonymous(token: $token) {
        auth {
          requestReset(email: $email, domainId: $domainId)
        }
      }
    }
  }
`;

export default function () {
  const location = useLocation();
  const domainId = new URLSearchParams(location.search).get("domainId");
  const [phase, setPhase] = useState("init");
  const { publicKey, ready } = useCaptcha();

  // Same guard as login: the mutation declares $domainId: String!, so a missing
  // one fails variable validation and replaces the page with a raw GraphQL error.
  if (!domainId) {
    return <MissingDomain />;
  }

  if (!ready) {
    return <Loading.Content />;
  }

  return (
    <Section.Provider schema={AuthSchema}>
      <Section.Mutation
        guard={false}
        context={{ domainId, phase }}
        initial={{ email: "" }}
        mutation={REQUEST_RESET}
        schema={AuthSchema}
        target={{ $ref: "/Input/Reset/Request/UI" }}
        onMutate={({ handler, state: { values } }) => {
          const { email } = values;

          setPhase("init");
          CaptchaLib.request(publicKey, (token) => {
            handler({
              variables: { email, domainId, token },
            });
          });
        }}
        onError={() => {
          setPhase("error");
        }}
        onCompleted={() => {
          // Deliberately the same screen whether or not the address is known —
          // the API answers true either way, and saying "no such account" here
          // would hand back the account enumeration the API refuses to give.
          setPhase("sent");
        }}
      >
        {MutationHandler}
      </Section.Mutation>
    </Section.Provider>
  );
};

const MutationHandler = ({
  schema,
  target,
  state: { values, setValues },
  context: { domainId, phase }
}) => {
  const formProps = {
    schema,
    target,
    values,
    onChange: (values) => {
      setValues(values);
    },
    config: {
      "/Input/Reset/Request/UI": {
        fields: [
          {
            label: "Email",
            property: "email",
          },
        ],
      },
    },
  };

  if (phase === "sent") {
    return (
      <Container className="mt-4">
        <Row>
          <Col className="text-center">
            <h1>Check your email</h1>
            <p>
              If that address has an account, a reset link is on its way. The
              link expires in one hour.
            </p>
          </Col>
        </Row>
        <Row className="mt-4">
          <Col className="text-center">
            <Link to={`/auth/login?domainId=${encodeURIComponent(domainId)}`}>
              Back to sign in
            </Link>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <Row>
        <Col className="text-center">
          <h1>Reset your password</h1>
          <p>Enter your email address and we will send you a reset link.</p>
        </Col>
      </Row>

      <Row>
        <Col>
          <div className="mt-4"></div>
          <Form {...formProps}>
            <Section.Button type={"submit"}>Send reset link</Section.Button>
          </Form>
        </Col>
      </Row>

      <Row className="mt-4">
        <Col>
          {phase === "error" && (
            <Alert color="danger">
              We could not send the reset link. Please try again, or contact
              support if the problem continues.
            </Alert>
          )}
        </Col>
      </Row>

      <Row>
        <Col className="text-center">
          <Link to={`/auth/login?domainId=${encodeURIComponent(domainId)}`}>
            Back to sign in
          </Link>
        </Col>
      </Row>
    </Container>
  );
};
